// `XMatrix3::GetRotation(XVector3& v, float& angle) const` 0x125b800 (XOM XMatrix3.cpp:627) in
// float32, operation for operation as the sim's port (crates/sim/src/core/math/xmatrix3.rs
// `GetRotation`): the axis and angle (radians) of a rotation matrix. The particle locators use it
// (`BaseParticleEffect::UpdateEmitterPositions` 0xd4cc90, particles.ts). Checked bit for bit
// against the original's recorded vectors by client/tests/xmatrix3.test.mjs.

const f = Math.fround;
/** 1e-6 (0x160b8c0), π (0x40490fdb). */
const EPS = f(1e-6), PI = f(Math.PI);

/** `el`: the row-vector matrix row by row (`el[3i + j]` = `el[i][j]`, row i the image of axis i;
 * for a three.js column-major 4x4 `e`: `el[3i + j]` = `e[4i + j]`). */
export function getRotation(el: ArrayLike<number>): { x: number; y: number; z: number; angle: number } {
  const [e00, e01, e02, e10, e11, e12, e20, e21, e22] = Array.from({ length: 9 }, (_, k) => f(el[k]));
  const cs = f(f(f(f(e00 + e11) + e22) - 1) * 0.5);
  // ucomiss cs, -1.0; jbe -> π (also unordered); ucomiss 1.0, cs; ja -> (float)acos((double)cs)
  const angle = cs <= -1 || Number.isNaN(cs) ? PI : 1 > cs ? f(Math.acos(cs)) : 0;
  const vx = f(e12 - e21), vy = f(e20 - e02), vz = f(e01 - e10);
  const len = f(Math.sqrt(f(f(vz * vz) + f(f(vy * vy) + f(vx * vx)))));
  if (len > EPS) {
    const inv = f(1 / len);
    return { x: f(vx * inv), y: f(vy * inv), z: f(inv * vz), angle };
  }
  if (!(angle > 1)) return { x: 1, y: 0, z: 0, angle };
  // The half-turn: the first sign pattern (+,+,+), (+,−,+), (+,−,−) with |M·s − s|² < 1e-6.
  const sx = f(Math.sqrt(f(f(e00 + 1) * 0.5))), sy = f(Math.sqrt(f(f(e11 + 1) * 0.5))), sz = f(Math.sqrt(f(f(1 + e22) * 0.5)));
  const p00 = f(e00 * sx), p10 = f(e10 * sx), p20 = f(e20 * sx);
  const p02 = f(e02 * sz), p12 = f(e12 * sz), p22 = f(sz * e22);
  let t0 = f(f(f(f(e01 * sy) + p00) + p02) - sx);
  let t1 = f(f(f(f(sy * e11) + p10) + p12) - sy);
  let t2 = f(f(f(f(sy * e21) + p20) + p22) - sz);
  if (EPS > f(f(f(t0 * t0) + f(t1 * t1)) + f(t2 * t2))) return { x: sx, y: sy, z: sz, angle };
  const ny = -sy;
  const u0 = f(f(e01 * ny) + p00), u1 = f(f(e11 * ny) + p10), u2 = f(p20 + f(e21 * ny));
  t0 = f(f(p02 + u0) - sx);
  t1 = f(f(p12 + u1) + sy);
  t2 = f(f(p22 + u2) - sz);
  if (EPS > f(f(t2 * t2) + f(f(t0 * t0) + f(t1 * t1)))) return { x: sx, y: ny, z: sz, angle };
  const nz = -sz;
  t0 = f(f(u0 + f(e02 * nz)) - sx);
  t1 = f(f(u1 + f(e12 * nz)) + sy);
  t2 = f(sz + f(u2 + f(nz * e22)));
  if (EPS > f(f(t2 * t2) + f(f(t1 * t1) + f(t0 * t0)))) return { x: sx, y: ny, z: nz, angle };
  return { x: sx, y: sy, z: nz, angle };
}
